import { Observable, of as observableOf } from "rxjs";

import { catchError, map, share, switchMap } from "rxjs/operators";

import { InitService } from "@core-services/init.service";
import { TradableWalletService } from "./tradable_wallet.service";
import { DerivativeWallet } from "@models/derivative/wallets/derivative_wallet";
import { Derivative } from "@models/derivative/currencies/derivative";
import { Address, ErrorClass } from "@models";
import { parseList } from "@helpers/functions";
import { DerivativeTransaction } from "@models/transaction";
import { GetPendingTradesGQLService } from "@gql/offer";
import { ApicallOption } from "@core-services/apicall.service";
import { DestructorService } from "@core-services/destructor.service";
import { ValidationError } from "bp-common-models-lib";

export interface DerivativeWalletViewHelper<DW extends DerivativeWallet> {
	wallet: DW;
	currentAddress: Address;
	otherAddresses: Address[];
}

export abstract class DerivativeWalletService<
	DW extends DerivativeWallet,
	D extends Derivative
> extends TradableWalletService<DW, DerivativeTransaction, D> {
	protected canCreateMore: boolean = false;
	protected cannotCreateReason: string = "";

	public TxType: typeof DerivativeTransaction;

	protected constructor(
		protected initService: InitService,
		protected destructor: DestructorService,
		public name: string,
		protected getPendingTradesGQLService: GetPendingTradesGQLService
	) {
		super(initService, destructor, name, getPendingTradesGQLService);
	}

	public confirmTransaction(
		walletId: string,
		transactionId: string,
		code: string
	): Observable<DerivativeTransaction> {
		return this.apicallService
			.get(`wallet/${walletId}/transaction/${transactionId}/confirm/${code}`)
			.pipe(switchMap(() => this.getTransaction(walletId, transactionId)));
	}

	public async cancel(t: DerivativeTransaction): Promise<DerivativeTransaction> {
		const oldTransaction = new DerivativeTransaction();
		Object.assign(oldTransaction, t);
		const call = this.apicallService.deleteRequest(
			"wallet/" + t.getWalletId() + "/transaction/" + t.getId(),
			new ApicallOption(DerivativeTransaction)
		);
		return super.cancelSub(call, t, oldTransaction).toPromise();
	}

	public createNewAddress(wallet: DW): Observable<DerivativeWalletViewHelper<DW>> {
		return this.apicallService.post(`wallet/${wallet.getId()}/addresses`, new ApicallOption(Address)).pipe(
			map((res) => {
				const addresses = res;
				return {
					wallet: wallet,
					currentAddress: addresses[addresses.length - 1],
					otherAddresses: addresses.slice(0, addresses.length - 1)
				};
			}),
			share()
		);
	}

	public getAddressesForWallet(wallet): Observable<DW> {
		const option = new ApicallOption();
		option.raw = true;
		return this.apicallService.get(`wallet/${wallet.getId()}/addresses`, option).pipe(
			map((addresses) => {
				this.canCreateMore = addresses["can_create_more"];
				this.cannotCreateReason = addresses["cannot_create_reason"] || "";

				wallet.addresses = parseList(addresses["data"], Address);
				return wallet;
			}),
			catchError((error) => {
				if (error instanceof ErrorClass) {
					if (
						error instanceof ValidationError &&
						error.containsCode("multichain.coin_network_id.no_coin_network_for_given_asset")
					) {
						return observableOf(null);
					}

					this.infoService.setError(error);
					return observableOf(null);
				}
				console.error(error);
				return [];
			})
		);
	}
}
